// Phase 175 — From/To date ranges for the targets tabs (IST business days, 'YYYY-MM-DD').
// Targets are stored per calendar month; the backend pro-rates them by the days of each month inside the range.
import { istDateISO } from "./api";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;                      // m = 1..12
export const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate(); // m = 1..12
const parts = (s) => s.split("-").map(Number);                           // 'YYYY-MM-DD' → [y, m, d]

export const RANGE_PRESETS = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "last_quarter", label: "Last quarter" },
  { key: "this_fy", label: "This financial year" },
  { key: "custom", label: "Custom range" },
];

/** Indian financial-year quarter (Apr–Jun = Q1 … Jan–Mar = Q4) containing month `m` (1..12) of year `y`. */
function fyQuarter(y, m) {
  const startMonth = m >= 4 ? 4 + Math.floor((m - 4) / 3) * 3 : 1;          // 4, 7, 10 or 1
  const endMonth = startMonth + 2;
  return { from: iso(y, startMonth, 1), to: iso(y, endMonth, daysInMonth(y, endMonth)) };
}

/** {from, to} for a preset, anchored on `today` (IST 'YYYY-MM-DD'). */
export function presetRange(key, today = istDateISO()) {
  const [y, m] = parts(today);
  switch (key) {
    case "last_month": {
      const ly = m === 1 ? y - 1 : y, lm = m === 1 ? 12 : m - 1;
      return { from: iso(ly, lm, 1), to: iso(ly, lm, daysInMonth(ly, lm)) };
    }
    case "this_quarter":
      return fyQuarter(y, m);
    case "last_quarter": {
      const q = fyQuarter(y, m);
      const [qy, qm] = parts(q.from);
      const py = qm === 1 ? qy - 1 : qy, pm = qm === 1 ? 10 : qm - 3;
      return fyQuarter(py, pm);
    }
    case "this_fy": {
      const fyStart = m >= 4 ? y : y - 1;
      return { from: iso(fyStart, 4, 1), to: iso(fyStart + 1, 3, 31) };
    }
    case "this_month":
    default:
      return { from: iso(y, m, 1), to: iso(y, m, daysInMonth(y, m)) };
  }
}

/** Which preset (if any) produces exactly this range today. */
export function detectPreset(range, today = istDateISO()) {
  for (const p of RANGE_PRESETS) {
    if (p.key === "custom") continue;
    const r = presetRange(p.key, today);
    if (r.from === range.from && r.to === range.to) return p.key;
  }
  return "custom";
}

/** 'YYYY-MM' of every month touched by the range, oldest first. */
export function monthsInRange(range) {
  const [fy, fm] = parts(range.from);
  const [ty, tm] = parts(range.to);
  const out = [];
  let y = fy, m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${pad(m)}`);
    m += 1;
    if (m === 13) { m = 1; y += 1; }
  }
  return out;
}

/** 'YYYY-MM' when the whole range sits inside one calendar month (full or partial), else null. */
export function singleMonthOf(range) {
  const ms = monthsInRange(range);
  return ms.length === 1 ? ms[0] : null;
}

/** true when the range starts on the 1st and ends on the last day of its end month. */
export function isFullMonths(range) {
  const [, , fd] = parts(range.from);
  const [ty, tm, td] = parts(range.to);
  return fd === 1 && td === daysInMonth(ty, tm);
}

export const monthLabel = (month, longYear = false) => {
  const [y, m] = parts(month);
  return `${MON[m - 1]} ${longYear ? y : String(y).slice(2)}`;
};

/** 'Oct 2025' · 'Oct – Dec 2025' · '15 Sep – 15 Oct 2025' — same wording as the backend label. */
export function rangeLabel(range) {
  if (!range?.from || !range?.to) return "";
  const [fy, fm, fd] = parts(range.from);
  const [ty, tm, td] = parts(range.to);
  if (isFullMonths(range)) {
    if (fy === ty && fm === tm) return `${MON[fm - 1]} ${fy}`;
    if (fy === ty) return `${MON[fm - 1]} – ${MON[tm - 1]} ${ty}`;
    return `${MON[fm - 1]} ${fy} – ${MON[tm - 1]} ${ty}`;
  }
  if (fy === ty) return `${fd} ${MON[fm - 1]} – ${td} ${MON[tm - 1]} ${ty}`;
  return `${fd} ${MON[fm - 1]} ${fy} – ${td} ${MON[tm - 1]} ${ty}`;
}

export const daysInRange = (range) => {
  const [fy, fm, fd] = parts(range.from);
  const [ty, tm, td] = parts(range.to);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000) + 1;
};

/** Previous calendar month of 'YYYY-MM'. */
export const prevMonth = (month) => {
  const [y, m] = parts(month);
  return m === 1 ? `${y - 1}-12` : `${y}-${pad(m - 1)}`;
};

/** Query params for the targets endpoints. */
export const rangeParams = (range) => ({ from: range.from, to: range.to });
